import { API_BASE_URL, API_ENDPOINTS } from "@/shared/apiClient";
import { AuthSessionError, authService } from "@/features/auth/services/authService";
import {
  transformProjectDataFromBackend,
  transformProjectInfoFromBackend,
} from "@/shared/utils/fieldNameTransform";
import { log } from "@/shared/utils/logger";
import type { ProjectData } from "@/shared/types/projectData";

export interface GitHubConfig {
  repository_url?: string;
  branch?: string;
  access_token?: string;
  sync_enabled?: boolean;
  has_token?: boolean;
  last_sync?: string;
}

export interface ProjectInfo {
  // Frontend uses camelCase exclusively after transformation
  // Backend sends snake_case → transformProjectInfoFromBackend() converts to camelCase
  projectId: string;
  projectName: string;
  description?: string;
  client?: string;
  projectAssigneeId?: string | null;
  leadAgentProfileId?: string | null;
  projectType?: 'proposal_application' | 'product_development' | 'research_report' | 'client_ops' | 'qa_release' | 'internal_admin' | 'legacy_cleanup' | null;
  workflowStatus?: 'unclassified' | 'suggested' | 'template_applied' | 'repair_proposed' | 'review_pending' | 'complete';
  companyName?: string;
  color?: string;
  isPinned?: boolean;
  isFavorite?: boolean;
  lastAccessed?: string;
  createdAt?: string;
  updatedAt?: string;
  nodeCount?: number;
  role?: "owner" | "editor" | "viewer";
  status?: "active" | "completed" | "on-hold" | "cancelled" | "archived";
  githubConfig?: GitHubConfig;
  projectSlug?: string;
}

export type ProjectCatalogLoadStatus = "ok" | "unauthorized" | "transient";

export interface SelfServiceOnboardingPayload {
  workspaceName: string;
  outcome: string;
  targetDate?: string | null;
  firstTaskTitles: string[];
  preferredStartView: "gantt" | "kanban" | "docs";
}

export interface SelfServiceOnboardingResult {
  project: ProjectData;
  onboardingStatus: "pending" | "completed" | "skipped";
  preferredStartView: "dashboard" | "gantt" | "kanban" | "docs";
}

interface ProjectCatalogLoadResult {
  projects: ProjectInfo[];
  status: ProjectCatalogLoadStatus;
}

class ProjectService {
  async listProjectsWithStatus(): Promise<ProjectCatalogLoadResult> {
    const authStatus = await authService.ensureValidTokenStatus();
    if (authStatus !== "valid") {
      log.warn("ProjectService", "Authentication is not ready for project catalog", {
        status: authStatus,
      });
      return {
        projects: [],
        status: authStatus === "unauthorized" ? "unauthorized" : "transient",
      };
    }

    // Check if we have a valid token before making the request
    const token = authService.getToken();
    if (!token) {
      log.warn("ProjectService", "No authentication token available after token validation");
      return { projects: [], status: "transient" };
    }

    // Validate token format before making request
    if (!authService.validateTokenFormat(token)) {
      log.error(
        "ProjectService",
        "Token format appears invalid; skipping request without clearing tokens"
      );
      return { projects: [], status: "unauthorized" };
    }

    // Log the request details for debugging
    // IMPORTANT: Exclude archived projects by default to show only active projects
    const url = `${API_BASE_URL}${API_ENDPOINTS.projects.list}?include_archived=false&summary_only=true`;
    log.debug("ProjectService", "Making request to:", url);
    log.debug("ProjectService", "With valid JWT token");

    try {
      const response = await authService.authenticatedFetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        // Try to get detailed error message from response body
        let errorDetail = "";
        try {
          const errorBody = await response.text();
          log.error("ProjectService", "Error response body:", errorBody);

          // Try to parse as JSON if possible
          try {
            const errorJson = JSON.parse(errorBody);
            errorDetail =
              errorJson.detail ||
              errorJson.message ||
              errorJson.error ||
              response.statusText;
          } catch {
            errorDetail = errorBody || response.statusText;
          }
        } catch {
          errorDetail = response.statusText;
        }

        log.error("ProjectService", "Failed to fetch projects:", {
          status: response.status,
          statusText: response.statusText,
          detail: errorDetail,
        });

        // Handle specific error codes
        if (response.status === 401) {
          return { projects: [], status: "unauthorized" };
        }

        if (response.status === 403) {
          log.warn(
            "ProjectService",
            "Authorization error (403) - insufficient permissions"
          );
          // Don't clear token, user is authenticated but lacks permissions
          return { projects: [], status: "unauthorized" };
        }

        if (response.status === 400) {
          log.warn("ProjectService", "Bad Request - checking token format");
          // Log token details for debugging
          if (token) {
            const parts = token.split(".");
            log.debug(
              "ProjectService",
              "Token parts:",
              parts.length,
              "(should be 3 for JWT)"
            );
            if (parts.length === 3) {
              try {
                const payload = JSON.parse(atob(parts[1]));
                log.debug("ProjectService", "Token payload:", {
                  exp: payload.exp ? new Date(payload.exp * 1000) : "no expiry",
                  iat: payload.iat
                    ? new Date(payload.iat * 1000)
                    : "no issue time",
                  sub: payload.sub || "no subject",
                  expired: payload.exp
                    ? Date.now() > payload.exp * 1000
                    : "unknown",
                });
              } catch (e) {
                log.error("ProjectService", "Failed to decode token:", e);
              }
            }
          }
          // Return empty array for now to prevent cascading errors
          return { projects: [], status: "unauthorized" };
        }

        throw new Error(`Failed to fetch projects: ${errorDetail}`);
      }

      const responseData = await response.json();
      // Extract projects array from response (handles both direct array and nested object formats)
      const projects = responseData.data?.projects || responseData.projects || responseData;
      // Transform each project to ensure consistent field names
      return { projects: projects.map(transformProjectInfoFromBackend), status: "ok" };
    } catch (error) {
      log.error("ProjectService", "Error fetching projects:", error);
      if (error instanceof AuthSessionError) {
        return {
          projects: [],
          status: error.kind === "unauthorized" ? "unauthorized" : "transient",
        };
      }
      // Return empty array instead of throwing to prevent cascading errors
      return { projects: [], status: "transient" };
    }
  }

  async listProjects(): Promise<ProjectInfo[]> {
    const result = await this.listProjectsWithStatus();
    return result.projects;
  }

  async createProject(
    data:
      | { name: string; description?: string }
      | { project_name: string; description?: string }
  ): Promise<ProjectInfo> {
    // Convert to the format expected by backend - it expects project_name not name
    const projectData =
      "project_name" in data
        ? {
            project_name: data.project_name,
            description: data.description || "",
          }
        : { project_name: data.name, description: data.description || "" };

    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.projects.create}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(projectData),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.detail ||
        errorData.message ||
        errorData.error ||
        (response.status === 403
          ? "You're not authorized to create projects. Ask an admin to grant project creation access."
          : "Failed to create project");
      throw new Error(errorMessage);
    }

    const payload = await response.json();
    const project = payload?.data?.project || payload?.project || payload?.data || payload;
    // Transform to ensure consistent field names
    return transformProjectInfoFromBackend(project);
  }

  async updateProject(
    projectId: string,
    updates: Partial<{
      project_name: string;
      description: string;
      client: string | null;
      project_assignee_id: string | null;
      lead_agent_profile_id: string | null;
      company_name: string | null;
      visibility_tier_default: 'internal' | 'partner' | 'client';
      project_type: 'proposal_application' | 'product_development' | 'research_report' | 'client_ops' | 'qa_release' | 'internal_admin' | 'legacy_cleanup' | null;
      workflow_status: 'unclassified' | 'suggested' | 'template_applied' | 'repair_proposed' | 'review_pending' | 'complete';
      apply_to_entire_project: boolean;
      github_config: GitHubConfig;
    }>
  ): Promise<ProjectInfo> {
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.projects.getById(projectId)}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.detail || errorData.message || errorData.error || "Failed to update project";
      throw new Error(errorMessage);
    }

    const payload = await response.json();
    const project = payload?.data?.project || payload?.project || payload?.data || payload;
    // Transform to ensure consistent field names
    return transformProjectInfoFromBackend(project);
  }

  async completeSelfServiceOnboarding(
    projectId: string,
    payload: SelfServiceOnboardingPayload
  ): Promise<SelfServiceOnboardingResult> {
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.projects.selfServiceOnboarding(projectId)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workspace_name: payload.workspaceName,
          outcome: payload.outcome,
          target_date: payload.targetDate || null,
          first_task_titles: payload.firstTaskTitles,
          preferred_start_view: payload.preferredStartView,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.detail || errorData.message || errorData.error || "Failed to complete workspace setup";
      throw new Error(errorMessage);
    }

    const result = await response.json();
    const data = result?.data || result;
    return {
      project: transformProjectDataFromBackend(data.project),
      onboardingStatus: (data.onboarding_status || data.onboardingStatus || "completed") as SelfServiceOnboardingResult["onboardingStatus"],
      preferredStartView: (data.preferred_start_view || data.preferredStartView || payload.preferredStartView) as SelfServiceOnboardingResult["preferredStartView"],
    };
  }

  async skipSelfServiceOnboarding(projectId: string): Promise<SelfServiceOnboardingResult> {
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.projects.selfServiceOnboardingSkip(projectId)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.detail || errorData.message || errorData.error || "Failed to skip workspace setup";
      throw new Error(errorMessage);
    }

    const result = await response.json();
    const data = result?.data || result;
    return {
      project: transformProjectDataFromBackend(data.project),
      onboardingStatus: (data.onboarding_status || data.onboardingStatus || "skipped") as SelfServiceOnboardingResult["onboardingStatus"],
      preferredStartView: (data.preferred_start_view || data.preferredStartView || "dashboard") as SelfServiceOnboardingResult["preferredStartView"],
    };
  }

  async archiveProject(projectId: string): Promise<void> {
    // This endpoint is intentionally named DELETE in the API, but it performs
    // the owner/admin soft-archive path by setting project status to archived.
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.projects.delete(projectId)}`,
      {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.detail || errorData.message || errorData.error || "Failed to archive project";
      throw new Error(errorMessage);
    }
  }

  async setProjectClient(
    projectId: string,
    clientUsername: string
  ): Promise<{ project_id: string; client: string }> {
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.projects.getById(projectId)}/client`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client: clientUsername }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.detail || errorData.message || errorData.error || "Failed to set project client";
      throw new Error(errorMessage);
    }

    const result = await response.json();
    // API returns { success, data: { project_id, client }, message }
    return result.data || result;
  }
}

export const projectService = new ProjectService();
